<template>
    <b-modal id="share-payment-link-modal" ref="share_modal" hide-footer title="Share Payment Link">
        <div v-if="checking" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
        </div>

        <div v-else-if="!dukakitEnabled" class="text-center py-2">
            <h5 class="mb-2">Dukakit Payments isn't enabled</h5>
            <p class="text-muted">To send secure payment links to your customers, enable Dukakit Payments in your payment settings.</p>
            <a href="/settings/payments" class="btn btn-primary">Go to Payment Settings</a>
        </div>

        <div v-else-if="mode === 'wallet_topup' && !link">
            <p class="text-center text-muted mb-3">How much should {{ walletCustomer && walletCustomer.full_name }} top up?</p>
            <div class="form-group">
                <label>Amount (KSh)</label>
                <input type="number" min="1" class="form-control" v-model.number="topupAmount" />
            </div>
            <button
                type="button"
                class="btn btn-primary btn-block"
                :disabled="!topupAmount || topupAmount <= 0 || creatingLink"
                @click="createWalletTopupLink"
            >
                <span v-if="creatingLink"><i class="bi bi-arrow-repeat spin"></i> Creating...</span>
                <span v-else>Create Link</span>
            </button>
            <small v-if="error" class="text-danger d-block mt-2">{{ error }}</small>
        </div>

        <div v-else-if="link">
            <p class="text-center text-muted mb-3">
                {{ link.reason }} &bull; <strong>KSh {{ formatMoney(link.amount) }}</strong>
            </p>

            <div class="form-group">
                <label>Recipient phone number</label>
                <vue-tel-input v-on:validate="validatePhone" v-model="phoneNumber"></vue-tel-input>
                <small v-if="phoneValidMessage" class="text-danger d-block mt-1">{{ phoneValidMessage }}</small>
            </div>

            <div class="d-flex mt-3">
                <button
                    type="button"
                    class="btn btn-outline-success flex-fill mr-2"
                    :disabled="!!phoneValidMessage || !phoneNumber"
                    @click="shareViaWhatsApp"
                >
                    <i class="bi bi-whatsapp mr-1"></i> WhatsApp
                </button>
                <button
                    type="button"
                    class="btn btn-outline-primary flex-fill"
                    :disabled="!!phoneValidMessage || !phoneNumber || sending"
                    @click="shareViaSms"
                >
                    <span v-if="sending" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="bi bi-chat-dots mr-1"></i> SMS
                </button>
            </div>

            <div class="text-center mt-3">
                <small class="text-muted d-block mb-1">Or copy the link directly</small>
                <div class="input-group input-group-sm">
                    <input type="text" class="form-control" readonly :value="link.public_url" />
                    <div class="input-group-append">
                        <button type="button" class="btn btn-outline-secondary" @click="copyLink">Copy</button>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center text-danger py-4">
            {{ error || 'Could not create the payment link.' }}
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'SharePaymentLinkModal',

    data() {
        return {
            checking: false,
            dukakitEnabled: null,
            mode: 'order', // 'order' | 'wallet_topup'
            link: null,
            error: '',
            phoneNumber: '',
            phoneValidMessage: '',
            sending: false,

            walletCustomer: null,
            topupAmount: null,
            creatingLink: false,
        };
    },

    methods: {
        async open(order, customer) {
            this.mode = 'order';
            this.link = null;
            this.error = '';
            this.phoneNumber = (customer && customer.phone) ? customer.phone : '';
            this.phoneValidMessage = '';

            const ready = await this.checkEnabledAndShow();
            if (!ready) return;

            try {
                const created = await axios.post('/api/payment-links', { order_id: order.id });
                this.link = created.data;
            } catch (err) {
                this.error = err.response?.data?.message || 'Could not create the payment link.';
            }
        },

        async openForWalletTopup(customer) {
            this.mode = 'wallet_topup';
            this.link = null;
            this.error = '';
            this.walletCustomer = customer;
            this.topupAmount = null;
            this.phoneNumber = (customer && customer.phone) ? customer.phone : '';
            this.phoneValidMessage = '';

            await this.checkEnabledAndShow();
        },

        async checkEnabledAndShow() {
            this.checking = true;
            this.$refs.share_modal.show();
            try {
                const { data } = await axios.get('/api/dukakit-payments/settings');
                this.dukakitEnabled = !!data.enabled;
                return this.dukakitEnabled;
            } catch (err) {
                this.error = 'Could not check payment settings.';
                return false;
            } finally {
                this.checking = false;
            }
        },

        async createWalletTopupLink() {
            this.creatingLink = true;
            this.error = '';
            try {
                const created = await axios.post('/api/payment-links', {
                    customer_id: this.walletCustomer.id,
                    amount: this.topupAmount,
                });
                this.link = created.data;
            } catch (err) {
                this.error = err.response?.data?.message || 'Could not create the payment link.';
            } finally {
                this.creatingLink = false;
            }
        },

        validatePhone(phoneNumber) {
            this.phoneValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },

        shareViaWhatsApp() {
            const normalized = this.phoneNumber.startsWith('0')
                ? '254' + this.phoneNumber.substring(1)
                : this.phoneNumber.replace(/^\+/, '').replace(/\s/g, '');
            window.open(`https://wa.me/${normalized}?text=${encodeURIComponent(this.link.message)}`, '_blank');
        },

        async shareViaSms() {
            this.sending = true;
            try {
                await axios.post(`/api/payment-links/${this.link.id}/send`, { phone_number: this.phoneNumber });
                this.$toasts.push({ type: 'success', message: 'Payment link sent via SMS.' });
                this.$refs.share_modal.hide();
            } catch (err) {
                this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not send SMS.' });
            } finally {
                this.sending = false;
            }
        },

        copyLink() {
            navigator.clipboard?.writeText(this.link.public_url);
            this.$toasts.push({ type: 'success', message: 'Link copied to clipboard.' });
        },

        formatMoney(v) {
            return Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
